import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, CircularProgress, Typography, Paper } from '@mui/material';
import { useCurrentScenarioData } from '../../state/hooks/ScenarioHooks';
import { useScenarioResetValues } from '../ScenarioParameters/ScenarioParametersContext';
import JointJSMap from './JointJsMap';
import { useDatasetMapInitialization } from './hooks/useDatasetMapInitialization';
import {
  useGraphInitialization,
  useGraphChangeHandler,
  useInitialGraph,
  DEFAULT_HEIGHT,
  DEFAULT_EMPTY_GRAPH,
  DEBOUNCE_DELAY,
} from './hooks/useInteractiveMap';

const InteractiveMapParameter = ({ parameterData, context, parameterValue, setParameterValue }) => {
  const { isLoading: isScenarioLoading } = useCurrentScenarioData();
  const scenarioResetValues = useScenarioResetValues();
  const currentScenarioData = useCurrentScenarioData();
  const {
    isLoading: isDatasetLoading,
    datasetError,
    datasetMapContent,
  } = useDatasetMapInitialization(parameterValue, setParameterValue, currentScenarioData);

  useGraphInitialization(parameterValue);
  const displayValue = parameterValue || datasetMapContent || JSON.stringify(DEFAULT_EMPTY_GRAPH);
  const initialGraphData = useInitialGraph(displayValue, scenarioResetValues);

  const initialSerializedRef = useRef(null);
  const initialObjectRef = useRef(null);

  useEffect(() => {
    if (initialSerializedRef.current !== null) return;
    try {
      if (typeof parameterValue === 'string' && parameterValue.trim()) {
        initialSerializedRef.current = parameterValue;
        initialObjectRef.current = JSON.parse(parameterValue);
      } else {
        const safe = {
          graph: { transport: initialGraphData?.graph?.transport ?? [] },
          cells: initialGraphData?.cells ?? [],
        };
        if (initialGraphData?.tables) safe.tables = initialGraphData.tables;
        initialSerializedRef.current = JSON.stringify(safe);
        initialObjectRef.current = safe;
      }
    } catch {
      initialSerializedRef.current = JSON.stringify(DEFAULT_EMPTY_GRAPH);
      initialObjectRef.current = DEFAULT_EMPTY_GRAPH;
    }
  }, [parameterValue, initialGraphData]);

  const [mapKey, setMapKey] = useState(0);

  const suppressWritesUntilTs = useRef(0);
  const baseHandleGraphChange = useGraphChangeHandler(context?.editMode, parameterValue, setParameterValue);
  const handleGraphChange = useCallback(
    (newGraphData) => {
      if (Date.now() < suppressWritesUntilTs.current) return;
      baseHandleGraphChange(newGraphData);
    },
    [baseHandleGraphChange]
  );

  const isTransitioning = useMemo(() => {
    const st = currentScenarioData?.state;
    return st === 'Running' || st === 'Failed' || st === 'Successful';
  }, [currentScenarioData?.state]);

  useEffect(() => {
    if (isTransitioning && !context?.isResetAction) return;

    const resetBaseline = scenarioResetValues?.interactiveMap;
    const currentStr = typeof parameterValue === 'string' ? parameterValue : safeStringify(parameterValue);
    const resetStr = typeof resetBaseline === 'string' ? resetBaseline : safeStringify(resetBaseline);
    if (!resetStr) return;

    if (currentStr && currentStr === resetStr && initialSerializedRef.current) {
      suppressWritesUntilTs.current = Date.now() + DEBOUNCE_DELAY + 50;

      setParameterValue(initialSerializedRef.current);

      setMapKey((k) => k + 1);
    }
  }, [isTransitioning, context?.isResetAction, parameterValue, scenarioResetValues, setParameterValue]);

  const isLoading = isScenarioLoading || isDatasetLoading;
  const readOnly = !context?.editMode;

  const height = useMemo(() => parameterData?.metadata?.height || DEFAULT_HEIGHT, [parameterData]);

  if (datasetError) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {datasetError}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} align="center">
          <CircularProgress size={24} />
        </Grid>
      </Grid>
    );
  }

  const initialGraphObject =
    (initialGraphData && Object.keys(initialGraphData).length ? initialGraphData : null) ||
    initialObjectRef.current ||
    DEFAULT_EMPTY_GRAPH;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JointJSMap
          key={mapKey}
          height={height}
          initialGraph={initialGraphObject}
          originalInitialGraph={initialObjectRef.current}
          onGraphChange={handleGraphChange}
          readOnly={readOnly}
        />
      </Grid>
    </Grid>
  );
};

function safeStringify(obj) {
  try {
    return obj ? JSON.stringify(obj) : '';
  } catch {
    return '';
  }
}

InteractiveMapParameter.propTypes = {
  parameterData: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  parameterValue: PropTypes.any,
  setParameterValue: PropTypes.func.isRequired,
};

export default React.memo(InteractiveMapParameter);
